import {
  DASHBOARD_API_BASE_URL,
  type DashboardApiResponse,
} from '../config/config';
import { ADMIN_TOKEN_KEY } from '../../auth/adminAuth';

/** A customer row as returned by GET/PATCH/POST /api/admin/customers/. */
export interface CustomerRecord {
  id: number | string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  country: string;
  role: string;
  auth_provider: string;
  is_verified: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  profile_full_name: string;
  profile_address: string;
  preferred_language: string;
  emergency_contact: string;
}

export interface CustomersListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: CustomerRecord[];
}

export interface CustomerListFilters {
  search?: string;
  isActive?: boolean;
  isVerified?: boolean;
  page?: number;
}

export interface CreateCustomerPayload {
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  country?: string;
  is_verified?: boolean;
  password?: string;
  profile_full_name?: string;
  profile_address?: string;
  preferred_language?: string;
  emergency_contact?: string;
}

export type UpdateCustomerPayload = Partial<CreateCustomerPayload>;

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);

  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const parseErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const result = await response.json().catch(() => null);

  if (result?.message) return result.message;
  if (result?.detail) return result.detail;

  return fallback;
};

/** GET /api/admin/customers/ */
export const apiGetCustomers = async (
  filters: CustomerListFilters = {}
): Promise<DashboardApiResponse<CustomersListResult>> => {
  try {
    const params = new URLSearchParams();

    if (filters.search) params.set('search', filters.search);
    if (filters.isActive !== undefined) params.set('is_active', String(filters.isActive));
    if (filters.isVerified !== undefined) params.set('is_verified', String(filters.isVerified));
    if (filters.page) params.set('page', String(filters.page));

    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/customers/?${params.toString()}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      return {
        success: false,
        message: await parseErrorMessage(response, 'Failed to fetch customers.'),
      };
    }

    const result = await response.json();

    return {
      success: true,
      message: 'Customers fetched successfully.',
      data: {
        count: result.count ?? 0,
        next: result.next ?? null,
        previous: result.previous ?? null,
        results: Array.isArray(result.results) ? result.results : [],
      },
    };
  } catch {
    return { success: false, message: 'Connection failed.' };
  }
};

/** GET /api/admin/customers/<id>/ */
export const apiGetCustomer = async (
  id: number | string
): Promise<DashboardApiResponse<CustomerRecord>> => {
  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/customers/${id}/`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      return {
        success: false,
        message: await parseErrorMessage(response, 'Failed to fetch customer.'),
      };
    }

    const data = await response.json();
    return { success: true, message: 'Customer fetched successfully.', data };
  } catch {
    return { success: false, message: 'Connection failed.' };
  }
};

/** POST /api/admin/customers/ */
export const apiCreateCustomer = async (
  payload: CreateCustomerPayload
): Promise<DashboardApiResponse<CustomerRecord>> => {
  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/customers/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Failed to create customer.',
      };
    }

    return {
      success: true,
      message: result?.message || 'Customer created successfully.',
      data: result?.data,
    };
  } catch {
    return { success: false, message: 'Connection failed.' };
  }
};

/** PATCH /api/admin/customers/<id>/ */
export const apiUpdateCustomer = async (
  id: number | string,
  payload: UpdateCustomerPayload
): Promise<DashboardApiResponse<CustomerRecord>> => {
  try {
    const response = await fetch(`${DASHBOARD_API_BASE_URL}/api/admin/customers/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Failed to update customer.',
      };
    }

    return {
      success: true,
      message: result?.message || 'Customer updated successfully.',
      data: result?.data,
    };
  } catch {
    return { success: false, message: 'Connection failed.' };
  }
};

/** PATCH /api/admin/customers/<id>/deactivate/ */
export const apiDeactivateCustomer = async (
  id: number | string
): Promise<DashboardApiResponse<CustomerRecord>> => {
  try {
    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/customers/${id}/deactivate/`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
      }
    );

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Failed to deactivate customer.',
      };
    }

    return {
      success: true,
      message: result?.message || 'Customer deactivated successfully.',
      data: result?.data,
    };
  } catch {
    return { success: false, message: 'Connection failed.' };
  }
};

/** PATCH /api/admin/customers/<id>/reactivate/ */
export const apiReactivateCustomer = async (
  id: number | string
): Promise<DashboardApiResponse<CustomerRecord>> => {
  try {
    const response = await fetch(
      `${DASHBOARD_API_BASE_URL}/api/admin/customers/${id}/reactivate/`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
      }
    );

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Failed to reactivate customer.',
      };
    }

    return {
      success: true,
      message: result?.message || 'Customer reactivated successfully.',
      data: result?.data,
    };
  } catch {
    return { success: false, message: 'Connection failed.' };
  }
};